import React, { useMemo } from 'react';
import './BuyersSelector.scss';

export interface Buyer {
    id: string | number;
    name: string;
    checked: boolean;
}

interface BuyersSelectorProps {
    buyers: Buyer[];
    selectAll: boolean;
    onBuyerChange: (buyerId: string | number, checked: boolean) => void;
    onSelectAllChange: (checked: boolean) => void;
    onBuyerNameClick?: (buyerId: string | number) => void;
    disabled?: boolean;
    readonly?: boolean;
    error?: string;
    /** When true, hide "Select All" (e.g. when showing only the proposer for seller editing proposal) */
    hideSelectAll?: boolean;
}

export const BuyersSelector: React.FC<BuyersSelectorProps> = ({
    buyers,
    selectAll,
    onBuyerChange,
    onSelectAllChange,
    onBuyerNameClick,
    disabled = false,
    readonly = false,
    error,
    hideSelectAll = false,
}) => {
    // Check if all buyers are manually selected
    const allBuyersChecked = useMemo(() => {
        if (buyers.length === 0) return false;
        return buyers.every(buyer => buyer.checked);
    }, [buyers]);

    // Determine if Select All should be checked
    const isSelectAllChecked = selectAll || allBuyersChecked;

    const handleSelectAllChange = (checked: boolean) => {
        onSelectAllChange(checked);
    };

    const handleBuyerChange = (buyerId: string | number, checked: boolean) => {
        onBuyerChange(buyerId, checked);
    };

    const handleBuyerNameClick = (e: React.MouseEvent, buyerId: string | number) => {
        e.preventDefault();
        e.stopPropagation();
        if (onBuyerNameClick) {
            onBuyerNameClick(buyerId);
        }
    };

    const hasError = !!error;
    const className = `buyers-selector ${hasError ? 'has-error' : ''}`;
    
    return (
        <div className={className}>
            <div className="buyers-header">
                <div className="buyers-label">
                    Buyers
                </div>
            </div>
            
            <div className="buyers-options">
                {/* Select All option */}
                {!hideSelectAll && (
                <div className="buyer-option">
                    <input
                        type="checkbox"
                        id="buyer-select-all"
                        checked={isSelectAllChecked}
                        onChange={(e) => handleSelectAllChange(e.target.checked)}
                        disabled={disabled || readonly}
                        className="buyer-checkbox"
                    />
                    <label 
                        htmlFor="buyer-select-all" 
                        className="buyer-checkbox-label"
                    >
                        Select All
                    </label>
                </div>
                )}

                {/* Individual buyers */}
                {buyers.map((buyer) => (
                    <div key={buyer.id} className="buyer-option">
                        <input
                            type="checkbox"
                            id={`buyer-${buyer.id}`}
                            checked={buyer.checked}
                            onChange={(e) => handleBuyerChange(buyer.id, e.target.checked)}
                            disabled={disabled || readonly}
                            className="buyer-checkbox"
                        />
                        <span 
                            className={`buyer-checkbox-label ${onBuyerNameClick ? 'clickable' : ''}`}
                            onClick={(e) => onBuyerNameClick && handleBuyerNameClick(e, buyer.id)}
                        >
                            {buyer.name}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

